import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { call, put, takeLatest } from "redux-saga/effects";
import { getUserByToken } from "./authCrud";

export const actionTypes = {
  Login: "[Login] Action",
  Logout: "[Logout] Action",
  Register: "[Register] Action",
  TokensRefreshed: "[Tokens Refreshed] Action",
  UserRequested: "[Request User] Action",
  UserLoaded: "[Load User] Auth API",
  SetUser: "[Set User] Action"
};

const initialAuthState = {
  user: undefined,
  authToken: undefined,
  refreshToken: undefined,
  idToken: undefined,
  accessTokenExpiresAt: undefined,
  oauthClientId: undefined
};

function accessTokenExpiresAtFromSeconds(expiresIn) {
  if (
    typeof expiresIn !== "number" ||
    !Number.isFinite(expiresIn) ||
    expiresIn <= 0
  ) {
    return undefined;
  }
  return Date.now() + Math.floor(expiresIn * 1000);
}

export const reducer = persistReducer(
  {
    storage,
    key: "v713-demo1-auth",
    whitelist: [
      "user",
      "authToken",
      "refreshToken",
      "idToken",
      "accessTokenExpiresAt",
      "oauthClientId"
    ]
  },
  (state = initialAuthState, action) => {
    switch (action.type) {
      case actionTypes.Login: {
        const { authToken, refreshToken, idToken, expiresIn, oauthClientId } = action.payload;

        return {
          authToken,
          refreshToken: refreshToken ?? undefined,
          idToken: idToken ?? undefined,
          accessTokenExpiresAt: accessTokenExpiresAtFromSeconds(expiresIn),
          oauthClientId: oauthClientId ?? undefined,
          user: state.user,
        };
      }

      case actionTypes.Register: {
        const { authToken, refreshToken, idToken, expiresIn } = action.payload;

        return {
          authToken,
          refreshToken: refreshToken ?? undefined,
          idToken: idToken ?? undefined,
          accessTokenExpiresAt: accessTokenExpiresAtFromSeconds(expiresIn),
          oauthClientId: undefined,
          user: undefined
        };
      }

      case actionTypes.TokensRefreshed: {
        const { authToken, refreshToken, idToken, expiresIn } = action.payload;

        return {
          ...state,
          authToken,
          refreshToken: refreshToken ?? state.refreshToken,
          idToken:
            typeof idToken === "string" && idToken
              ? idToken
              : state.idToken,
          accessTokenExpiresAt: accessTokenExpiresAtFromSeconds(expiresIn),
        };
      }

      case actionTypes.Logout: {
        // TODO: Change this code. Actions in reducer aren't allowed.
        return initialAuthState;
      }

      case actionTypes.UserLoaded: {
        const { user } = action.payload;
        return { ...state, user };
      }

      case actionTypes.SetUser: {
        const { user } = action.payload;
        return { ...state, user };
      }

      default:
        return state;
    }
  }
);

export const actions = {
  login: (authToken, refreshToken, expiresIn, idToken, oauthClientId) => ({
    type: actionTypes.Login,
    payload: { authToken, refreshToken, expiresIn, idToken, oauthClientId }
  }),
  register: (authToken, refreshToken, expiresIn, idToken) => ({
    type: actionTypes.Register,
    payload: { authToken, refreshToken, expiresIn, idToken }
  }),
  refreshTokens: (authToken, refreshToken, expiresIn, idToken) => ({
    type: actionTypes.TokensRefreshed,
    payload: { authToken, refreshToken, expiresIn, idToken }
  }),
  logout: () => ({ type: actionTypes.Logout }),
  requestUser: user => ({
    type: actionTypes.UserRequested,
    payload: { user }
  }),
  fulfillUser: user => ({ type: actionTypes.UserLoaded, payload: { user } }),
  setUser: user => ({ type: actionTypes.SetUser, payload: { user } })
};

export function* userRequested() {
  try {
    const { data: user } = yield call(getUserByToken);

    yield put(actions.fulfillUser(user));
  } catch (error) {
    yield put(actions.logout());
  }
}

export function* saga() {
  yield takeLatest(actionTypes.Login, function* loginSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.Register, function* registerSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.UserRequested, userRequested);
}
